import React, { useContext, useEffect, useState } from 'react';
import {
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  format,
  getDay,
} from 'date-fns';
import { DevicesContext } from '../../contexts/DevicesContext';
import HelperFunctions from '../../utils/helperFunctions';

interface DeviceDataHeatMap {
  parameterValue: number;
  recordedAt: string;
}

interface YearlyAQICalendarProps {
  year: number;
  deviceHeatmapData: DeviceDataHeatMap[];
  // deviceId: number;
  setSelectedMonth: React.Dispatch<React.SetStateAction<number>>;
  isMobile: boolean;
  setViewMode: (_view : "currentmonth" | "currentyear") => void;
}

interface ApiData {
  parameterValue: number;
  recordedAt: string;
}

const YearlyAQICalendar: React.FC<YearlyAQICalendarProps> = ({
  year,
  deviceHeatmapData,
  // deviceId,
  setSelectedMonth,
  isMobile,
  setViewMode
}) => {
  const devicesContext = useContext(DevicesContext);
  if (!devicesContext) {
    throw new Error('useDevices must be used within a DevicesProvider');
  }

  // const { deviceHeatmapData, getDeviceHeatmapData } = devicesContext;
  const months = Array.from({ length: 12 }, (_, i) => i);
  const convertToCalendarData = (
    apiData: ApiData[]
  ): Record<string, number> => {
    const result: Record<string, number> = {};
    apiData.forEach(item => {
      const dateKey = format(new Date(item.recordedAt), 'yyyy-MM-dd');
      result[dateKey] = item.parameterValue;
    });
    return result;
  };

  const [heatmapData, setHeatmapData] = useState<Record<string, number>>({});
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number; placement: string }>({ top: 0, left: 0, placement: 'right' });

  // Auto-dismiss tooltip after 2 seconds
  useEffect(() => {
    if (!activeTooltip) return;
    const timer = setTimeout(() => setActiveTooltip(null), 2000);
    return () => clearTimeout(timer);
  }, [activeTooltip]);

  // Dismiss tooltip on scroll or outside tap
  useEffect(() => {
    if (!activeTooltip) return;
    const dismiss = () => setActiveTooltip(null);
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('touchstart', dismiss, { passive: true });
    return () => {
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('touchstart', dismiss);
    };
  }, [activeTooltip]);
  const [currentMonth, setCurrentMonth] = useState<number>(
    year === new Date().getFullYear() ? new Date().getMonth() : 0
  );

  // useEffect(() => {
  //   const getHeatmapData = async () => {
  //     try {
  //       await getDeviceHeatmapData(
  //         Number(deviceId),
  //         new Date(year, 0, 1).toISOString().split('T')[0],
  //         new Date(year, 11, 31).toISOString().split('T')[0]
  //       );
  //     } catch (error) {
  //       console.error('Error checking devices:', error);
  //     }
  //   };

  //   getHeatmapData();
  // }, [year]);

  useEffect(() => {
    setHeatmapData(convertToCalendarData(deviceHeatmapData));
  }, [deviceHeatmapData]);

  // Reset to current month for current year, January for other years
  useEffect(() => {
    setCurrentMonth(year === new Date().getFullYear() ? new Date().getMonth() : 0);
  }, [year]);

  const handleMonthChange = (direction: 'prev' | 'next') => {
    setCurrentMonth((prev) => {
      if (direction === 'prev') {
        return prev === 0 ? 11 : prev - 1;
      }
      if (direction === 'next') {
        return prev === 11 ? 0 : prev + 1;
      }
      return prev;
    });
  };

  const handleGoToMonthClick = () => {
    setSelectedMonth(currentMonth + 1);
    setViewMode('currentmonth');
  };

  return (
    <div className="year-heatmap-container gap-6 overflow-visible yearly-heatmap-container">
      {isMobile ? (
        <>
        <div
          className="border border-[var(--button-border-color)] rounded-2xl shadow p-3 bg-[#FBFBFB] dark:bg-[#FBFBFB33] cursor-pointer overflow-visible"
          // onClick={() => setSelectedMonth(currentMonth)}
        >
          <div className="flex justify-between items-center mb-2">
            <button
              onClick={() => handleMonthChange('prev')}
              className="p-2 text-[var(--color-sub-title-text)] text-xl font-bold"
            >
              &lt;
            </button>
            <h2
              className="text-center text-[var(--color-text)]"
              onClick={handleGoToMonthClick}
            >
              {format(startOfMonth(new Date(year, currentMonth)), 'MMM yyyy')}
            </h2>
            <button
              onClick={() => handleMonthChange('next')}
              className="p-2 text-[var(--color-sub-title-text)] text-xl font-bold"
            >
              &gt;
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-xs place-items-center">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <div key={d} className="text-center font-medium text-[var(--color-text)]">
                {d}
              </div>
            ))}
            {Array.from({ length: getDay(startOfMonth(new Date(year, currentMonth))) }).map((_, i) => (
              <div key={`pad-${i}`} />
            ))}
            {eachDayOfInterval({
              start: startOfMonth(new Date(year, currentMonth)),
              end: endOfMonth(new Date(year, currentMonth)),
            }).map(day => {
              const key = format(day, 'yyyy-MM-dd');
              const value = heatmapData[key];
              return (
                <div key={key} className="relative group">
                  <div
                    className={`h-8 w-8 flex items-center justify-center rounded ${HelperFunctions.GetParameterColor(
                      value, 'AQI'
                    )} text-[10px] text-[var(--color-text)] bg-[#f1f1f1] dark:bg-[#FBFBFB33] font-bold`}
                    style={{
                      backgroundColor: value != null ? HelperFunctions.GetParameterColor(value, 'AQI') : '',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const rect = e.currentTarget.getBoundingClientRect();
                      const tooltipWidth = 60;
                      const screenWidth = window.innerWidth;
                      let pos: { top: number; left: number; placement: string };

                      if (rect.right + tooltipWidth + 8 < screenWidth) {
                        // Right
                        pos = { top: rect.top + rect.height / 2, left: rect.right + 4, placement: 'right' };
                      } else if (rect.left - tooltipWidth - 8 > 0) {
                        // Left
                        pos = { top: rect.top + rect.height / 2, left: rect.left - 4, placement: 'left' };
                      } else {
                        // Above
                        pos = { top: rect.top - 4, left: rect.left + rect.width / 2, placement: 'top' };
                      }

                      setActiveTooltip(activeTooltip === key ? null : key);
                      setTooltipPos(pos as any);
                    }}
                  >
                    {format(day, 'd')}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {activeTooltip && (
          <div
            style={{
              position: 'fixed',
              top: tooltipPos.top,
              left: tooltipPos.left,
              transform: tooltipPos.placement === 'right' ? 'translateY(-50%)'
                : tooltipPos.placement === 'left' ? 'translate(-100%, -50%)'
                : 'translate(-50%, -100%)',
              backgroundColor: '#363636',
              color: 'white',
              fontSize: '10px',
              fontWeight: 'bold',
              padding: '4px 8px',
              borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              whiteSpace: 'nowrap',
              zIndex: 99999,
              pointerEvents: 'none',
            }}
          >
            {heatmapData[activeTooltip] != null
              ? `${heatmapData[activeTooltip].toFixed(0)}`
              : 'No data'}
          </div>
        )}
        </>
      ) : (
        months.map(month => {
          const start = startOfMonth(new Date(year, month));
          const end = endOfMonth(start);
          const days = eachDayOfInterval({ start, end });
          const padding = getDay(start); // empty slots before first day

          return (
            <div
              key={month}
              className="border border-[var(--button-border-color)] rounded-2xl p-3 bg-[#FBFBFB] dark:bg-[#FBFBFB33] cursor-pointer"
              onClick={() => {
                setSelectedMonth(month + 1);
                setViewMode('currentmonth');
              }}
            >
              <h2 className="text-center text-[var(--color-text)] mb-2">
                {format(start, 'MMM yyyy')}
              </h2>
              <div className="grid grid-cols-7 gap-1 text-xs">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                  <div key={d} className="text-center font-normal text-[var(--color-sub-title-text)]">
                    {d}
                  </div>
                ))}
                {Array.from({ length: padding }).map((_, i) => (
                  <div key={`pad-${i}`} />
                ))}
                {days.map(day => {
                  const key = format(day, 'yyyy-MM-dd');
                  const value = heatmapData[key];
                  return (
                    <div key={key} className="relative group">
                      <div
                        className={`h-8 w-8 flex items-center justify-center rounded ${HelperFunctions.GetParameterColor(
                          value, 'AQI'
                        )} text-[10px] text-[var(--color-text)] bg-[#f1f1f1] dark:bg-[#FBFBFB33] font-bold`}
                        style={{
                          backgroundColor: value != null ? HelperFunctions.GetParameterColor(value, 'AQI') : '',
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {format(day, 'd')}
                      </div>
                      <div className="absolute bottom-full mb-0 left-[60%] opacity-0 group-hover:opacity-100 transition-opacity text-[10px] font-bold px-2 py-1 rounded shadow-lg whitespace-nowrap z-10 pointer-events-none"
                      style={{
                        transform: 'translate(0.25rem, 1.5rem)',
                        backgroundColor:'#363636',
                        color:'white',
                      }}>
                        {value != null ? `${value.toFixed(0)}` : 'No data'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default YearlyAQICalendar;
